import { Download, FileText, ShieldCheck } from "lucide-react";
import { trackBlogAssetDownload } from "@/lib/analytics";

export const US_CHINA_AI_BRIEFING_PDF_URL = "/media/us-china-ai-business-briefing-2026_8988fcc2.pdf";

interface USChinaAIBriefingLeadMagnetProps {
  postSlug: string;
}

/**
 * A direct-download, source-qualified companion briefing. It is deliberately
 * scoped to planning context and does not promise a commercial outcome.
 */
export default function USChinaAIBriefingLeadMagnet({ postSlug }: USChinaAIBriefingLeadMagnetProps) {
  const handleDownload = () => {
    trackBlogAssetDownload({
      asset_name: "us_china_ai_business_briefing_2026",
      asset_url: US_CHINA_AI_BRIEFING_PDF_URL,
      post_slug: postSlug,
      placement: "post_introduction",
    });
  };

  return (
    <section
      className="my-8 overflow-hidden rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 via-slate-950/5 to-amber-500/10"
      aria-labelledby="us-china-ai-briefing-title"
      data-testid="us-china-ai-briefing-lead-magnet"
    >
      <div className="h-1 bg-gradient-to-r from-cyan-500 via-teal-400 to-amber-400" />
      <div className="p-6 sm:p-7">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-300">
              <FileText className="h-4 w-4" aria-hidden="true" />
              Free cited executive briefing
            </div>
            <h2 id="us-china-ai-briefing-title" className="font-serif text-2xl font-semibold tracking-tight text-foreground">
              U.S.–China AI briefing: a practical guide for business leaders
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Download the six-page, source-qualified companion guide with a 30-day response plan, direct source links and QR codes to the full analysis and free audit.
            </p>
            <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-cyan-600" aria-hidden="true" />
              The briefing is informational. It does not predict rankings, citations, leads, revenue, job outcomes or AI capability milestones.
            </p>
          </div>

          <a
            href={US_CHINA_AI_BRIEFING_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleDownload}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-semibold text-slate-950 shadow-lg shadow-amber-500/20 transition-colors hover:bg-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Download the cited guide
          </a>
        </div>
      </div>
    </section>
  );
}
